import { useMemo } from 'react';
import { useAuth } from 'context/AuthContext';
import { canViewAllTasks, isManagerUser } from 'utils/helpers/access/hasUserAccess';
import { TaskProvider } from '../TaskContext';
import { loadBoardFilters } from './helpers/boardFiltersStorage';
import {
    snapshotToLead,
    snapshotToObject,
    snapshotToUser,
} from './Kanban/helpers/boardFilterSnapshots';
import BoardBody, { BOARD_EXCLUDE_STATUSES } from './BoardBody';

const Board = () => {
    const { auth } = useAuth();
    const userId = Number(auth?.user?.ID || 0);
    const canFilterByUsers = canViewAllTasks(auth?.user);
    const canFilterRelated = isManagerUser(auth?.user);
    const storedFilters = useMemo(
        () => loadBoardFilters(userId, { excludeStatuses: BOARD_EXCLUDE_STATUSES }),
        [userId],
    );

    const initialExecutorUser = useMemo(
        () =>
            canFilterByUsers
                ? snapshotToUser(storedFilters.executorUsers?.[0] ?? null)
                : null,
        [canFilterByUsers, storedFilters.executorUsers],
    );
    const initialControllerUser = useMemo(
        () =>
            canFilterByUsers
                ? snapshotToUser(storedFilters.controllerUsers?.[0] ?? null)
                : null,
        [canFilterByUsers, storedFilters.controllerUsers],
    );
    const initialLead = useMemo(
        () =>
            canFilterRelated && !storedFilters.onlyPersonal
                ? snapshotToLead(storedFilters.lead)
                : null,
        [canFilterRelated, storedFilters.lead, storedFilters.onlyPersonal],
    );
    const initialObject = useMemo(
        () =>
            canFilterRelated && !storedFilters.onlyPersonal
                ? snapshotToObject(storedFilters.object)
                : null,
        [canFilterRelated, storedFilters.object, storedFilters.onlyPersonal],
    );

    return (
        <TaskProvider
            key={userId}
            noShowOlderTask
            noShowDeletedTask
            defaultOnPage={100}
            initialTasksStatus={storedFilters.status}
            initialTasksType={storedFilters.type}
            initialTasksExecutor={canFilterByUsers ? storedFilters.executor : null}
            initialTasksController={canFilterByUsers ? storedFilters.controller : null}
            initialFilterLeadId={
                canFilterRelated && !storedFilters.onlyPersonal ? storedFilters.leadId : null
            }
            initialFilterObjectId={
                canFilterRelated && !storedFilters.onlyPersonal ? storedFilters.objectId : null
            }
            initialOnlyPersonal={storedFilters.onlyPersonal}
        >
            <BoardBody
                initialExecutorUser={initialExecutorUser}
                initialControllerUser={initialControllerUser}
                initialLead={initialLead}
                initialObject={initialObject}
                canFilterByUsers={canFilterByUsers}
            />
        </TaskProvider>
    );
};

export default Board;
